/*
 * Copyright Deskshare Inc.
 * All rights reserved.
 *
 * This software is proprietary and confidential.
 * Unauthorized copying, distribution, or use is strictly prohibited.
 */

/**
 * Refunds API Service
 *
 * S7 (Pass 8): Added eligibility endpoint call and free-text reason.
 */

import { createApi } from "@reduxjs/toolkit/query/react";
import {
  baseQueryWithReauth,
  CACHE_TIMES,
  TAG_TYPES,
} from "@/redux/api-config";

/**
 * S7 (Pass 8): Eligibility check request — server is the single authority.
 */
export interface CheckEligibilityRequest {
  payment_intent: string;
  /** B4 (FCR S497): optional — enables per-product refund limit check */
  product_id?: string;
  /**
   * VD-3 (Pass 20): without this the server cannot resolve per-product amounts
   * on newer Stripe API shapes, which moved the invoice off the charge and
   * payment-intent objects. The server has always read it; there was no field
   * to send it in, so eligibility fell through every resolution step, computed
   * no amount, and the dialog failed closed on around four in ten per-product
   * refunds. The submit call already sends it — this makes the two match.
   */
  metadata?: { invoice_id?: string };
}

/**
 * Eligibility response from the server (S5).
 *
 * Issue 3 (FCR Pass 9 Correction): window_days is the refund window the server
 *   enforces. The browser prefers this over its own local constant so the
 *   wording stays in sync when the policy is tuned via environment variables.
 *
 * N-2 (FCR Pass 9 Correction): charge_disputed added — the server now returns
 *   this reason when the charge is under a bank dispute.
 *
 * Issue 1 (FCR Pass 9 Correction): document_translator_no_cash_refund added —
 *   the server returns this when a Document Translator purchase is attempted.
 *
 * EX-19 (S578): consumable_no_cash_refund added — the same rule now covers a
 *   category rather than one product, so Extra Words word packs are refused for
 *   the same reason. The two codes are kept apart so neither message can end up
 *   describing a purchase the customer did not make.
 *
 * N-7 (FCR Pass 9 Correction): eligibility_check_failed is set by the browser
 *   (never by the server) when the eligibility request itself fails.
 */
export interface CheckEligibilityResponse {
  eligible: boolean;
  /** Machine-readable reason when not eligible */
  reason?:
    | "window_expired"
    | "already_refunded"
    | "abuse_limit"
    | "nothing_charged"
    | "no_payment_intent"
    | "superseded_by_upgrade"
    | "product_refund_limit_reached"
    | "charge_disputed"                      // N-2 (FCR Pass 9 Correction)
    | "document_translator_no_cash_refund"   // Issue 1 (FCR Pass 9 Correction)
    | "consumable_no_cash_refund"            // EX-19 (S578) — word packs; words are spent on use
    | "eligibility_check_failed"             // N-7 — set by browser on network failure; also sent by server when refund count check fails
    | "count_unavailable"                    // Audit Pass 4 — server sends when Stripe refund count API is unreachable during submit
    | "superseded_by_upgrade"               // Audit Pass 4 — already in eligibility; also returned as 422 from create_refund
    | "amount_unverifiable"                 // Audit Pass 4 — server cannot determine refundable amount for this product
    // SU-2 (FCR Pass 10): create_refund returns this as a 409 when the submitted
    // figure exceeds the refundable line total. It was already being sent and had
    // no entry here, so it fell through every wording branch and the customer saw
    // the generic fallback sentence instead of a description of what happened.
    | "amount_mismatch"                     // the amount shown no longer matches what can be refunded
    // SU-12 (FCR Pass 9): these two used to share the "already_refunded" wording,
    // so a customer with an unrefunded product was told the order was already
    // refunded, and support could not tell the three cases apart.
    | "charge_balance_exhausted"            // the charge shows nothing left to refund
    | "refund_in_progress";                 // a refund for this product is already in flight
  days_remaining?: number;
  days_since?: number;
  /** B4 (FCR S497): set when reason is product_refund_limit_reached */
  product_id?: string;
  /**
   * Issue 3 (FCR Pass 9 Correction): The refund window in days the server
   * enforces. Prefer this over the browser's local REFUND_WINDOW_DAYS constant
   * so the wording stays correct when the policy is tuned via environment vars.
   */
  window_days?: number;
  /**
   * AU-2 / H-3 (FCR S557): the refundable amount for the requested product, in
   * cents, decided by the server. The dialog displays this figure and sends it
   * back with the refund request, so the customer is refunded exactly the
   * number they were shown.
   *
   * Null when the server could not resolve it -- the dialog then falls back to
   * the figure on the invoice line, and the refund request itself still fails
   * closed if the amount cannot be verified at that point.
   */
  refundable_amount_cents?: number | null;
  /**
   * VD-4 (Pass 20): the currency of refundable_amount_cents, stated by the
   * server that computed it. The dialog used to format that figure with the
   * invoice currency, which showed a EUR amount with a USD symbol on
   * discounted multi-currency orders.
   */
  refundable_currency?: string;
  /** What the customer's card statement shows. Null when not applicable. */
  refundable_presentment_amount_cents?: number | null;
  refundable_presentment_currency?: string | null;
}

/**
 * Request payload for creating a refund.
 * S7 (Pass 8): reason is now a free string (not a fixed union) so the
 * customer-chosen reason from the dropdown passes through unchanged.
 */
export interface CreateRefundRequest {
  /** Payment Intent ID to refund */
  payment_intent: string;
  /** Optional: Amount to refund in cents (if partial refund) */
  amount?: number;
  /**
   * S7 (Pass 8): Widened to string — the customer's chosen reason from the
   * dropdown. The server maps this to the appropriate Stripe reason enum.
   */
  reason?: string;
  /** Optional: Free-text note from the customer */
  reason_note?: string;
  /**
   * SK-I3 B (FCR S505): Stripe product ID of the specific line to refund on
   * a multi-product invoice. When present, the backend scopes the Stripe
   * refund amount to that product's line amount and filters license revocation
   * to only the matching QLM key. Omit for full-invoice refunds.
   */
  product_id?: string;
  /** Optional: Additional metadata */
  metadata?: Record<string, string>;
}

/**
 * Response from Stripe refund creation
 */
export interface RefundResponse {
  id: string;
  object: "refund";
  amount: number;
  balance_transaction: string | null;
  charge: string | null;
  created: number;
  currency: string;
  description: string | null;
  payment_intent: string | null;
  reason: string | null;
  receipt_number: string | null;
  status: "pending" | "succeeded" | "failed" | "canceled";
}

export interface RefundError {
  error: string;
  code?: string;
  details?: Record<string, unknown>;
}

export const refundsApi = createApi({
  reducerPath: "refundsApi",
  baseQuery: baseQueryWithReauth,
  tagTypes: [TAG_TYPES.ORDERS, TAG_TYPES.INVOICES],
  endpoints: (builder) => ({
    /**
     * S5/S7 (Pass 8): Check refund eligibility from the server before showing
     * options to the customer. The server is the single authority on the
     * 14-day window, ownership, and the abuse limit (N-6).
     */
    checkEligibility: builder.mutation<
      CheckEligibilityResponse,
      CheckEligibilityRequest
    >({
      query: (body) => ({
        url: "refund/check-eligibility/",
        method: "POST",
        body,
      }),
      // Eligibility is read-only — no cache invalidation needed.
    }),

    /**
     * Create a new refund for a payment.
     */
    createRefund: builder.mutation<RefundResponse, CreateRefundRequest>({
      query: (body) => ({
        url: "refund/",
        method: "POST",
        body,
      }),
      // Invalidate orders cache after successful refund so the billing table refreshes.
      invalidatesTags: [
        { type: TAG_TYPES.ORDERS, id: "LIST" },
        { type: TAG_TYPES.INVOICES, id: "LIST" },
      ],
    }),

    /**
     * Get refund details by ID.
     */
    getRefund: builder.query<RefundResponse, string>({
      query: (refundId) => `refund/${refundId}/`,
      keepUnusedDataFor: CACHE_TIMES.LONG,
    }),
  }),
});

export const {
  useCheckEligibilityMutation,
  useCreateRefundMutation,
  useGetRefundQuery,
  useLazyGetRefundQuery,
} = refundsApi;
